import Modal from '@ui-components-3/ui/lib/components/Modal'
import ItemDetailsModalValue from './ItemDetailsModalValue'
import { observer } from 'mobx-react-lite'
import { format } from 'date-fns/format'
import { useMemo, useState } from 'react'
import convertSpecialty from 'util/convertSpecialty'
import { phoneInputFormat } from 'util/formatters'
import CancelMedicationModal from 'components/ModalsContainer/v1Modals/CancelMedication'
import {
  getJoinedInteractionsDrugs,
  getMedicationInteractionSeverityLevelUiConfig,
  getOrderedDrugInteractions,
} from 'util/medicationInteractions'
import { MedicationDrugInteractionsData } from 'types/Medications'
import Badge from 'components/common/Badge'

const DrugInteractionItem = ({ drugInteraction }: { drugInteraction: MedicationDrugInteractionsData }) => {
  const { drugs, severityLevel } = drugInteraction

  const { badgeVariant, label } = useMemo(
    () => getMedicationInteractionSeverityLevelUiConfig(severityLevel),
    [severityLevel],
  )

  const displayDrugs = useMemo(() => getJoinedInteractionsDrugs(drugs), [drugs])

  return (
    <p className="typography-body-l text-neutral-800 font-bold">
      <Badge className="mr-3" variant={badgeVariant}>
        {label}
      </Badge>
      {displayDrugs}
    </p>
  )
}

type PrescriptionDetailsModalProps = {
  // TODO - improve rx anv visit types
  rx: any
  onClose: () => void
  removeMedication: (prescriptionId: string, reason?: string) => Promise<void>
  hideCancel?: boolean
}

const PrescriptionDetailsModal = ({ rx, onClose, removeMedication, hideCancel }: PrescriptionDetailsModalProps) => {
  const [isCancelDialogVisible, setIsCancelDialogVisible] = useState(false)

  const {
    name,
    unit,
    dispenseUnit,
    quantity,
    refills,
    directions,
    pharmacyNotes,
    pharmacy,
    prescriptionId,
    removalForbidden,
    displayStatus,
    substitutions,
    isPending,
    writtenDate,
    interaction,
    interactionOverrideReason,
    interactionOverrideReasonNote,
  } = rx || {}

  const displayWrittenDate = writtenDate ? format(new Date(writtenDate), 'MM/dd/yyyy') : ''

  const orderedInteractions = useMemo(() => getOrderedDrugInteractions(interaction?.drugInteractions), [interaction])

  const displayInteractionOverrideReason = useMemo(
    () => [interactionOverrideReason, interactionOverrideReasonNote].filter(Boolean).join('\n'),
    [interactionOverrideReason, interactionOverrideReasonNote],
  )

  const displayPharmacy = useMemo(() => {
    if (!pharmacy) {
      return null
    }

    const { address1, city, state, zip, phone, specialty, storeName } = pharmacy || {}

    return [
      [storeName, convertSpecialty(specialty)].filter(Boolean).join(', '),
      [address1, city, state, zip].filter(Boolean).join(', '),
      phoneInputFormat(phone),
    ]
      .filter(Boolean)
      .join('\n')
  }, [pharmacy])

  const cancelMedication = async (values) => {
    setIsCancelDialogVisible(false)
    await removeMedication?.(prescriptionId, values?.cancellationReason)
    onClose?.()
  }

  const cancelLabel = `${isPending ? 'Delete' : 'Cancel'} Prescription`

  if (!rx) {
    return null
  }

  return (
    <Modal
      label="Prescription Details"
      opened
      footerVariant="right"
      footer={({ close }) => (
        <>
          {!removalForbidden && !hideCancel && (
            <button type="button" className="btn btn-fuchsia-outlined" onClick={() => setIsCancelDialogVisible(true)}>
              {cancelLabel}
            </button>
          )}
          <button type="button" className="btn btn-neutral" onClick={close}>
            Close
          </button>
        </>
      )}
      onClose={onClose}
    >
      <div className="flex w-full flex-col gap-4">
        <ItemDetailsModalValue label="Prescription" value={name} />
        <div className="flex items-start gap-4">
          <ItemDetailsModalValue label="Strength*" value={unit} />
          <ItemDetailsModalValue label="Dispense Unit*" value={dispenseUnit?.name} />
        </div>
        <div className="flex items-start gap-4">
          <ItemDetailsModalValue label="Quantity*" value={quantity} />
          <ItemDetailsModalValue label="Refills*" value={refills} />
        </div>
        <div className="flex items-start gap-4">
          <ItemDetailsModalValue label="Pharmacy" value={displayPharmacy} />
          <ItemDetailsModalValue label="Submitted Date" value={displayWrittenDate} />
        </div>
        <ItemDetailsModalValue label="Direction" value={directions} />
        <ItemDetailsModalValue label="Pharmacy Notes" value={pharmacyNotes} />
        <div className="flex items-start gap-4">
          <ItemDetailsModalValue label="Status" value={<span className="capitalize">{displayStatus}</span>} />
          <ItemDetailsModalValue label="Allow Substitutions" value={substitutions ? 'Yes, Allow' : 'No'} />
        </div>
        {!!orderedInteractions?.length && (
          <>
            <div className="flex flex-col gap-2">
              <p className="typography-body-l text-neutral-800">Medication Interactions</p>
              <div className="flex flex-col gap-2">
                {orderedInteractions.map((item, index) => (
                  <DrugInteractionItem key={String(index)} drugInteraction={item} />
                ))}
              </div>
            </div>
            <ItemDetailsModalValue label="Override Reason*" value={displayInteractionOverrideReason} />
          </>
        )}
      </div>
      {!!isCancelDialogVisible && !removalForbidden && !hideCancel && (
        <CancelMedicationModal cancelMedication={cancelMedication} onClose={() => setIsCancelDialogVisible(false)} />
      )}
    </Modal>
  )
}

export default observer(PrescriptionDetailsModal)
